import { TooltipOption } from "echarts/types/dist/shared";
import { EvaChartStates } from "types/factory";

import { getSeriesTooltipDot } from "components/charts/utils";
import { getSeriesNameWithFormattedUwi } from "components/charts/utils/getSeriesNameWithFormattedUWI";
import { formatNumber } from "components/charts/utils/precisionUtils";

import {
  getForecastSourceTooltipLine,
  getTooltipPositionWithXFieldSelector
} from "./utils";

export function translateProbitResponseTooltipOptions(
  states: EvaChartStates
): TooltipOption {
  const {
    uwis,
    hoverLegendItemRef,
    useProbitLineOfBestFitData,
    series,
    uwiCoordinates,
    defaultSignificantFigures,
    useStrictRounding,
    chartTypeParam
  } = states;

  // Extract dataTypes for x and y axes
  const xDataType = chartTypeParam?.properties?.x?.dataType;
  const yDataType = chartTypeParam?.properties?.y?.dataType;

  return {
    trigger: "axis",
    confine: true,
    enterable: true,
    extraCssText: "border-style:none;",
    hideDelay: 0,
    show: true,
    axisPointer: {
      label: {
        precision: 2,
        show: true
      },
      type: "cross"
    },
    formatter: (param) => {
      const val = hoverLegendItemRef?.current;
      if (val) {
        if (val?.includes(" Fit - ")) {
          const separated = val.split(" Fit - ");
          const seriesName = separated[0];
          const contentData = separated[1];
          const color =
            series.find((s) => s.label === seriesName)?.style.hexColor ?? param[0].color;
          return createBestFitTooltip(
            seriesName,
            contentData,
            color,
            useProbitLineOfBestFitData?.active,
            (value: number) =>
              formatNumber(value, defaultSignificantFigures, useStrictRounding, yDataType)
          );
        } else {
          const coordMap = uwiCoordinates as Record<
            string,
            { x: number; y: number; group: string }
          >;
          const uwiData = coordMap[val];
          const seriesName = uwiData?.group;
          if (!seriesName) {
            return "";
          }
          const data = formatNumber(
            uwiData.x,
            defaultSignificantFigures,
            useStrictRounding,
            xDataType
          );
          const colorList: Record<string, string> = {};
          for (const s of series) {
            colorList[s.label] = s.style.hexColor;
          }

          // Find all UWIs stacked at the exact same (x, y) position (e.g. multiple zeros).
          // Pin the hovered UWI (val) first so the tooltip always leads with what was hovered.
          const stackedUwis = [
            val,
            ...Object.entries(coordMap)
              .filter(
                ([uwi, coords]) =>
                  uwi !== val && coords.x === uwiData.x && coords.y === uwiData.y
              )
              .map(([uwi]) => uwi)
          ];

          if (stackedUwis.length > 1) {
            return createStackedUwiTooltip(stackedUwis, colorList, coordMap, data);
          }

          const color = seriesName in colorList ? colorList[seriesName] : "";
          return createSeriesDataTooltip(val, color, seriesName, data, uwiCoordinates);
        }
      } else {
        const series = param[0];

        if (series.seriesName && series.seriesName.includes(" Fit - ")) {
          return null;
        } else {
          const data =
            series.data.length > 0
              ? formatNumber(
                  series.data[0],
                  defaultSignificantFigures,
                  useStrictRounding,
                  xDataType
                )
              : "";
          return createSeriesDataTooltip(
            uwis[series.data[2]],
            series.color !== "none" ? series.color : series.borderColor,
            series.seriesName,
            data,
            uwiCoordinates
          );
        }
      }
    },
    position: getTooltipPositionWithXFieldSelector
  };
}

function createSeriesDataTooltip(uwi, color, seriesName, data, uwiCoordinates?) {
  return `<span style="color:#666";>${getSeriesNameWithFormattedUwi(uwi)}</span>
                ${getForecastSourceTooltipLine(uwiCoordinates, uwi)}
                <br/>
                <span style="color:#666";>
                ${getSeriesTooltipDot(color)}
                ${seriesName}
                </span>
                <br/>
                <p style="font-family: var(--fontMono);">${data}</p>`;
}

function createStackedUwiTooltip(
  uwis: string[],
  colorList: Record<string, string>,
  uwiCoordinates: Record<string, { x: number; y: number; group: string }>,
  data: string
) {
  const firstUwi = uwis[0];
  const seriesName = uwiCoordinates[firstUwi]?.group ?? "";
  const color = colorList[seriesName] ?? "";
  const extra = uwis.length - 1;
  const uwiLabel = `${getSeriesNameWithFormattedUwi(firstUwi)} +${extra} more`;
  return `<span style="color:#666";>${uwiLabel}</span>
                ${getForecastSourceTooltipLine(uwiCoordinates, firstUwi)}
                <br/>
                <span style="color:#666";>
                ${getSeriesTooltipDot(color)}
                ${seriesName}
                </span>
                <br/>
                <p style="font-family: var(--fontMono);">${data}</p>`;
}

function createBestFitTooltip(
  seriesName,
  contentData,
  color,
  isProbitDataFromLineOfBestFit,
  formatNumber: (value: number) => string
) {
  const content = contentData.split(",").map((value) => value.split(":"));

  const header = `<p style="text-align:center;">
                    ${getSeriesTooltipDot(color)} ${seriesName}
                  </p>`;

  const rowContent = (label, data) => {
    const numericValue = parseFloat(data);
    const formattedData = !isNaN(numericValue) ? formatNumber(numericValue) : data;
    return `<tr><td style="text-align:left;padding-right:30px;">${label}</td><td style="font-weight:700;color:#666;text-align:right;font-family: var(--fontMono);">${formattedData}</td></tr>`;
  };

  const body = `
                <table style="width:100%;">
                  ${content.map((data) => rowContent(data[0], data[1])).join("")}
                </table>
                    <span style="font-size:10px;">Calculated from ${
                      isProbitDataFromLineOfBestFit ? "line of best fit" : "data"
                    }.</span>`;

  return `${header}${body}`;
}
